import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Activity, Menu } from 'lucide-react';
import { useChatLayout } from '../../components/layout/ChatLayout';
import BusinessFilter from '../../components/layout/BusinessFilter';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { AccountCard } from '../../components/pulse/AccountCard';
import { pulseApi, type PulseAccount, type PulseAccountsResponse } from '../../services/pulse-api';

/**
 * Pulse — accounts tab (Screen A). Spans every business in the workspace (R11),
 * grouped into active / eligible / ineligible buckets, narrowable to one business.
 */
const PulseAccountsPage: React.FC = () => {
  const { isMobile, toggleSidebar } = useChatLayout();
  const [orgFilter, setOrgFilter] = useState<string | null>(null);
  const { data, isLoading, error } = useQuery({
    queryKey: ['pulse', 'accounts'],
    queryFn: pulseApi.listAccounts,
    refetchInterval: 30_000,
  });

  return (
    <div className="flex flex-col min-h-screen px-4 py-8 sm:py-12 relative">
      {isMobile && (
        <button
          onClick={toggleSidebar}
          className="absolute top-4 left-4 p-2 rounded-lg text-muted-foreground hover:bg-accent"
        >
          <Menu className="w-5 h-5" />
        </button>
      )}

      <div className="w-full max-w-4xl mx-auto space-y-8">
        <PulseHeader />
        <PulseTabs current="accounts" />

        <BusinessFilter value={orgFilter} onChange={setOrgFilter} />

        {isLoading && (
          <div className="flex justify-center py-16">
            <LoadingSpinner size="lg" />
          </div>
        )}

        {error && (
          <div className="rounded-xl bg-destructive/15 border border-destructive/20 text-destructive p-4">
            Failed to load accounts. Try again in a moment.
          </div>
        )}

        {data && <AccountGroups data={narrowToBusiness(data, orgFilter)} />}
      </div>
    </div>
  );
};

/** Client-side narrowing: the whole workspace is already loaded (R11). */
function narrowToBusiness(
  data: PulseAccountsResponse,
  orgId: string | null,
): PulseAccountsResponse {
  if (!orgId) return data;
  const keep = (rows: PulseAccount[]) => rows.filter((a) => a.organizationId === orgId);
  return {
    active: keep(data.active),
    paused: keep(data.paused),
    eligible: keep(data.eligible),
    ineligible: keep(data.ineligible),
  };
}

const AccountGroups: React.FC<{ data: PulseAccountsResponse }> = ({ data }) => {
  // Paused accounts live in the Active section (with a "Paused" badge on the card).
  const monitored = [...data.active, ...data.paused];
  const hasAny = monitored.length + data.eligible.length + data.ineligible.length > 0;
  if (!hasAny) {
    return (
      <div className="rounded-xl bg-muted border border-border p-8 text-center">
        <p className="text-muted-foreground">No accounts connected yet.</p>
        <p className="text-sm text-muted-foreground mt-1">
          Connect a data source to start monitoring it with Pulse.
        </p>
      </div>
    );
  }

  return (
    <>
      <Group title="Active" subtitle={count(monitored.length, 'account')}>
        {monitored.map((a) => (
          <AccountCard key={a.accountId} account={a} />
        ))}
      </Group>

      <Group title="Ready to activate" subtitle={count(data.eligible.length, 'eligible account')}>
        <div className="grid sm:grid-cols-2 gap-3">
          {data.eligible.map((a) => (
            <AccountCard key={a.accountId} account={a} />
          ))}
        </div>
      </Group>

      <Group title="Not eligible" subtitle={count(data.ineligible.length, 'account')}>
        <div className="grid sm:grid-cols-2 gap-3">
          {data.ineligible.map((a) => (
            <AccountCard key={a.accountId} account={a} />
          ))}
        </div>
      </Group>
    </>
  );
};

const Group: React.FC<{ title: string; subtitle: string; children: React.ReactNode }> = ({
  title,
  subtitle,
  children,
}) => {
  // Cheap empty-state suppression: don't render the section if no children.
  const hasChildren = React.Children.count(children) > 0;
  if (!hasChildren) return null;
  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          {title}
        </h2>
        <span className="text-xs text-muted-foreground">{subtitle}</span>
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  );
};

function count(n: number, noun: string): string {
  return `${n} ${noun}${n === 1 ? '' : 's'}`;
}

// ---------------------------------------------------------------------------
// Shared Pulse header + tabs (used by Accounts + Feed pages)
// ---------------------------------------------------------------------------

export const PulseHeader: React.FC = () => (
  <div className="flex items-center gap-3">
    <Activity className="w-8 h-8 text-primary" />
    <div>
      <h1 className="text-3xl font-bold text-foreground">Pulse</h1>
      <p className="text-muted-foreground mt-1">
        Automatic monitoring of your transaction data — Pulse watches for unusual gaps, volume,
        and value across every account.
      </p>
    </div>
  </div>
);

export const PulseTabs: React.FC<{ current: 'accounts' | 'feed' }> = () => (
  <div className="flex gap-1 border-b border-border">
    <PulseTab to="/pulse" label="Accounts" />
    <PulseTab to="/pulse/feed" label="Anomaly feed" />
  </div>
);

const PulseTab: React.FC<{ to: string; label: string }> = ({ to, label }) => (
  <NavLink
    to={to}
    end
    className={({ isActive }) =>
      `px-4 py-2.5 text-sm font-medium border-b-2 -mb-px ${
        isActive
          ? 'border-primary text-primary'
          : 'border-transparent text-muted-foreground hover:text-foreground'
      }`
    }
  >
    {label}
  </NavLink>
);

export default PulseAccountsPage;
